"use client";

import { Fragment, useState } from "react";
import { Detail, ItemCard, ItemView, LOAD_BASE, strengthOf, strengthToCarry } from "./characterViews";

export type EquipAction = "on" | "off" | "drop" | "keep" | "reset";
/** 着ける先の層。部位の名と、部位の中の層の番号 */
export interface EquipWhere {
  part: string;
  layer: number;
}

/** 所持品の絞り込み。分類の綴りとボタンの名。綴りが null のボタンは全部を出す */
const FILTERS: [string | null, string][] = [
  [null, "全部"],
  ["equipment", "装備"],
  ["usable", "使用"],
  ["ammunition", "弾"],
  ["medicine", "薬"],
  ["food", "食物"],
  ["drink", "飲物"],
  ["material", "素材"],
  ["tool", "道具"],
  ["document", "書類"],
  ["pass", "証"],
  ["treasure", "財宝"],
];

/**
 * インベントリのタブ。中を装備（部位ごとに層と着けている物）と所持品（積載と、装備していない物）のタブに分ける。
 * 装備している物は所持品に出さない。所持品は分類のボタンで絞り込む。開いたときは全部を出す。
 * 装備は付け替えを決めた後の形で出す。決めた付け替えと破棄は次の物語に入り、それまで本体は変わらない。
 * onEquip を渡すと、操作を出す。付け替えは装備のタブの層ごとの 変更 から行い、開いた欄で外す物か、所持品のうちその層に着けられる物を選ぶ。
 * 捨てる は所持品の物を開いた中に置く。onEquip は失敗の文を返す
 */
export default function PlayInventory({ detail, onEquip }: { detail: Detail; onEquip?: (action: EquipAction, id?: string, where?: EquipWhere) => Promise<string | null> }) {
  const byId = new Map(detail.items.map((item) => [item.id, item]));
  const plan = detail.equip_plan ?? null;
  const equipment = plan?.equipment ?? detail.equipment;
  const worn = new Set(equipment.flatMap((part) => part.layers.flatMap((layer) => layer.ids ?? [])));
  const off = new Set(plan?.off ?? []);
  const on = new Set(plan?.on ?? []);
  const drop = new Set(plan?.drop ?? []);
  const carried = detail.items.filter((item) => (plan?.equipment ? !worn.has(item.id) : !item.equipped));
  const load = detail.status?.load ?? null;
  const str = strengthOf(detail.status);
  /** 積載の上限のうち袋などの加算の分。上限 ＝ 70 ＋ 筋力 ＋ 加算 */
  const loadBonus = load && str !== null ? load.max - LOAD_BASE - str : null;
  const [sub, setSub] = useState<"equip" | "carried">("equip");
  /** 絞り込む分類の綴り。null は全部 */
  const [kind, setKind] = useState<string | null>(null);
  const shown = kind ? carried.filter((item) => item.category_keys?.includes(kind)) : carried;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  /** 変更の欄を開いている層。「{部位の名}/{層の番号}」 */
  const [picking, setPicking] = useState<string | null>(null);
  const open = Boolean(onEquip) && plan?.open === true;
  const pending = off.size + on.size + drop.size > 0;
  const names = (ids: Set<string>) => [...ids].map((id) => byId.get(id)?.name ?? id).join("・");

  const act = async (action: EquipAction, id?: string, where?: EquipWhere) => {
    if (!onEquip || busy) return;
    setBusy(true);
    setNote("");
    const error = await onEquip(action, id, where);
    setBusy(false);
    if (error) setNote(error);
    else setPicking(null);
  };

  /** 変更の欄。今着けている物を外すか、所持品のうちこの層に着けられる物を選ぶ */
  const picker = (where: EquipWhere, ids: string[], accepts: string[]) => {
    const current = ids.map((id) => byId.get(id)).filter((item): item is ItemView => Boolean(item));
    const candidates = carried.filter((item) => item.equip?.type_key && accepts.includes(item.equip.type_key) && !drop.has(item.id));
    return (
      <div className="pl-inv-pick">
        {current.filter((item) => !item.equip?.locked).map((item) => (
          <button key={item.id} type="button" className="pl-tab pl-inv-pick-off" disabled={busy} onClick={() => act("off", item.id)}>
            {item.name}を外す
          </button>
        ))}
        <p className="pl-inv-pick-head">着ける物を選ぶ</p>
        {candidates.length === 0 ? <p className="pl-inv-pick-none">ここに着けられる物を持っていません。</p> : null}
        {candidates.map((item) => {
          const weak = str !== null && str < strengthToCarry(item.bulk);
          return (
            <div className="pl-inv-row" key={item.id}>
              <ItemCard item={item} plain str={str} />
              <button type="button" className="pl-tab pl-inv-act" disabled={busy || weak} title={weak ? `筋力が ${strengthToCarry(item.bulk)} 要る` : undefined} onClick={() => act("on", item.id, where)}>
                {weak ? "筋力不足" : "着ける"}
              </button>
            </div>
          );
        })}
        <button type="button" className="pl-tab pl-inv-pick-close" disabled={busy} onClick={() => setPicking(null)}>
          閉じる
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="pl-subtabs" role="tablist">
        {([["equip", "装備"], ["carried", "所持品"]] as const).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={sub === key} className={sub === key ? "pl-subtab pl-subtab-on" : "pl-subtab"} onClick={() => setSub(key)}>
            {label}
          </button>
        ))}
      </div>
      {pending ? (
        <div className="pl-inv-plan">
          <p className="pl-inv-plan-head">次の物語に入る変更</p>
          {off.size > 0 ? <p className="pl-inv-plan-line">外す：{names(off)}</p> : null}
          {on.size > 0 ? <p className="pl-inv-plan-line">着ける：{names(on)}</p> : null}
          {drop.size > 0 ? <p className="pl-inv-plan-line">捨てる：{names(drop)}</p> : null}
          {open ? (
            <button type="button" className="pl-tab pl-inv-act" disabled={busy} onClick={() => act("reset")}>
              元に戻す
            </button>
          ) : null}
        </div>
      ) : null}
      {onEquip && plan && !plan.open ? <p className="pl-inv-closed">行動を入力できない間は、装備の付け替えと持ち物の破棄ができません。</p> : null}
      {note ? <p className="pl-inv-closed review-warn">{note}</p> : null}
      {sub === "equip" ? (
      <section className="pl-prof-sec pl-inv-equip">
        {equipment.length === 0 ? <p className="pl-prof-p">部位がありません。</p> : null}
        {equipment.map((part) => (
          <div className="pl-inv-part" key={part.part}>
            <p className="pl-inv-part-name">{part.part}</p>
            {part.layers.map((layer, index) => {
              const ids = layer.ids ?? [];
              const key = `${part.part}/${index}`;
              // 替えられる層だけに 変更 を出す。外せる物を着けているか、着ける口が在る層
              const canOff = ids.some((id) => byId.get(id)?.equip?.locked !== true);
              const canOn = (layer.accepts ?? []).length > 0 && (layer.capacity ?? 0) >= 1 && (ids.length < (layer.capacity ?? 0) || canOff);
              const changeable = open && part.blocked !== true && (canOff || canOn);
              return (
                <Fragment key={index}>
                  <div className={changeable ? "pl-inv-layer pl-inv-layer-edit" : "pl-inv-layer"}>
                    <span className="pl-inv-layer-name">{layer.layer}</span>
                    <div className="pl-inv-layer-items">
                      {layer.items.length === 0 ? <span className="pl-inv-none">なし</span> : null}
                      {layer.items.map((name, i) => {
                        const item = ids[i] ? byId.get(ids[i]) : undefined;
                        return item ? <ItemCard item={item} key={item.id} plain str={str} tag={on.has(item.id) ? "着ける予定" : undefined} /> : <span key={i}>{name}</span>;
                      })}
                    </div>
                    {changeable ? (
                      <button type="button" className={picking === key ? "pl-tab pl-tab-on pl-inv-act" : "pl-tab pl-inv-act"} disabled={busy} aria-expanded={picking === key} onClick={() => setPicking(picking === key ? null : key)}>
                        変更
                      </button>
                    ) : null}
                  </div>
                  {changeable && picking === key ? picker({ part: part.part, layer: index }, ids, layer.accepts ?? []) : null}
                </Fragment>
              );
            })}
          </div>
        ))}
      </section>
      ) : (
      <section className="pl-prof-sec pl-inv-carried">
        {load ? (
          <p className="pl-inv-load">
            積載 <b>{load.current}</b> / {load.max}
            {loadBonus !== null && loadBonus >= 0 ? (
              <small className="pl-inv-load-sum">
                （{LOAD_BASE} ＋ 筋力 {str}
                {loadBonus > 0 ? ` ＋ 袋 ${loadBonus}` : ""}）
              </small>
            ) : null}
          </p>
        ) : null}
        {carried.length > 0 ? (
          <div className="pl-seg pl-inv-filter" role="radiogroup" aria-label="所持品の分類">
            {FILTERS.map(([key, label]) => (
              <button key={label} type="button" role="radio" aria-checked={kind === key} className={kind === key ? "pl-seg-on" : ""} onClick={() => setKind(key)}>
                {label}
              </button>
            ))}
          </div>
        ) : null}
        {carried.length === 0 ? <p className="pl-prof-p">所持品はありません。</p> : null}
        {carried.length > 0 && shown.length === 0 ? <p className="pl-prof-p">この分類の所持品はありません。</p> : null}
        {shown.map((item) => (
          <ItemCard item={item} key={item.id} str={str} tag={drop.has(item.id) ? "捨てる予定" : off.has(item.id) ? "外す予定" : undefined}>
            {open && drop.has(item.id) ? (
              <button type="button" className="pl-tab pl-inv-drop" disabled={busy} onClick={() => act("keep", item.id)}>
                捨てるのをやめる
              </button>
            ) : null}
            {open && !drop.has(item.id) && !item.equip?.locked ? (
              <button type="button" className="pl-tab pl-inv-drop" disabled={busy} onClick={() => act("drop", item.id)}>
                捨てる
              </button>
            ) : null}
          </ItemCard>
        ))}
      </section>
      )}
    </div>
  );
}
